// Decides whether to show the OnboardingFlow / contract re-accept modal
// based on the session's contractAcceptance + onboarding state.
//
// Trigger logic:
//   - needsAcceptance && lastAcceptedVersion === null
//     → first sign-in → full OnboardingFlow (Welcome → Contract → Profile
//       → First-framework → Done)
//   - needsAcceptance && lastAcceptedVersion !== null
//     → versioned re-accept → reAccept-only modal (contract panel only,
//       not the full flow)
//   - !needsAcceptance
//     → no gate. (Profile-incomplete reminder is V1.x.)
//
// On completion, the gate calls onComplete() which the parent uses to
// refetch /auth/me + hide the modal.

import { useSession } from '../auth/index.js';
import { CoachingContractView } from './CoachingContractView.js';
import { OnboardingFlow } from './OnboardingFlow.js';
import styles from './OnboardingFlow.module.css';

export interface OnboardingGateProps {
  /** Called when the gate has nothing more to ask of the user. Parent
   *  should refetch /auth/me to update the session and re-render. */
  onComplete: () => void;
}

export function OnboardingGate({ onComplete }: OnboardingGateProps) {
  const { session } = useSession();

  if (!session || session.portal !== 'consumer') return null;
  const ca = session.contractAcceptance;
  if (!ca || !ca.needsAcceptance) return null;

  // First-time onboarding — never accepted before.
  if (ca.lastAcceptedVersion === null) {
    return <OnboardingFlow onComplete={onComplete} />;
  }

  // Versioned re-accept — already accepted an older version, contract has
  // bumped. Show the contract panel in reAccept mode, not the full flow.
  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <CoachingContractView mode="reAccept" onAccepted={onComplete} />
      </div>
    </div>
  );
}
